/**
 * App-root owner of URL writes for persisted search params. Renders nothing.
 * Evaluates every declaration on each address change; writes only on the
 * reconciler's trigger moments. Idle-deferred, replace, preventScrollReset.
 *
 * Mounted after the page tree so this effect runs after Tabs cleanup. Sibling
 * effects run in tree order; an earlier navigate would drop an activation fill.
 */

import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigation, useSearchParams } from "react-router";
import {
  enqueueSearchParamRemovals,
  enqueueSearchParamWrite,
  scheduleSearchParamsFlush,
} from "src/hooks/searchParamsWriteQueue";
import { reconcile } from "src/storage/persistedSearchParams/createPersistedSearchParams.reconcile";
import {
  getPersistedSearchParamsSnapshot,
  subscribePersistedSearchParams,
  subscribePersistedSearchParamsReset,
} from "src/storage/persistedSearchParams/createPersistedSearchParams.registry";

type SetSearchParams = ReturnType<typeof useSearchParams>[1];

const URL_DELTA_NAVIGATE_OPTIONS = {
  replace: true,
  preventScrollReset: true,
} as const;

/**
 * Goes through the shared write queue rather than navigating on its own: Tabs cleanup and
 * the hooks queue their strips in the same tick, and a competing navigation would drop
 * one side. Removals are queued first so a fill for a name the reconciler also strips
 * still wins.
 */
const applyUrlDelta = (
  setSearchParams: SetSearchParams,
  toSet: Record<string, string>,
  toRemove: string[]
): void => {
  if (!toRemove.length && !Object.keys(toSet).length) return;

  enqueueSearchParamRemovals(toRemove, URL_DELTA_NAVIGATE_OPTIONS);

  for (const [name, value] of Object.entries(toSet)) {
    enqueueSearchParamWrite(name, value, URL_DELTA_NAVIGATE_OPTIONS);
  }

  scheduleSearchParamsFlush(() => setSearchParams);
};

export default function PersistedSearchParamsSync() {
  const location = useLocation();
  const navigation = useNavigation();
  const [, setSearchParams] = useSearchParams();
  const [registryEpoch, setRegistryEpoch] = useState(0);
  const previousActiveIdsRef = useRef<string[]>([]);
  const previousPathnameRef = useRef<string | undefined>(undefined);
  const pendingResetRef = useRef(false);

  useEffect(
    () =>
      subscribePersistedSearchParams(() => setRegistryEpoch((epoch) => epoch + 1)),
    []
  );

  useEffect(
    () =>
      subscribePersistedSearchParamsReset(() => {
        pendingResetRef.current = true;
        setRegistryEpoch((epoch) => epoch + 1);
      }),
    []
  );

  useEffect(() => {
    void registryEpoch;
    // Loaders rewrite search; writing mid-navigation fights them and resets scroll.
    if (navigation.state !== "idle") return;

    const declarations = getPersistedSearchParamsSnapshot();
    const params = new URLSearchParams(location.search);
    const stored = Object.fromEntries(
      declarations.map((declaration) => [
        declaration.id,
        declaration.getStored?.() ?? {},
      ])
    );

    const result = reconcile({
      declarations,
      previousActiveIds: previousActiveIdsRef.current,
      previousPathname: previousPathnameRef.current,
      pathname: location.pathname,
      params,
      stored,
    });

    previousActiveIdsRef.current = result.activeIds;
    previousPathnameRef.current = location.pathname;

    let { toSet, toRemove } = result;
    if (pendingResetRef.current) {
      pendingResetRef.current = false;
      const resetNames = declarations
        .filter((declaration) => result.activeIds.includes(declaration.id))
        .flatMap((declaration) => declaration.params.map((entry) => entry.param));
      toRemove = [...new Set([...toRemove, ...resetNames])];
      toSet = {};
    } else {
      for (const [id, slice] of Object.entries(result.toStore)) {
        declarations
          .find((declaration) => declaration.id === id)
          ?.patchStored?.(slice);
      }
    }

    applyUrlDelta(setSearchParams, toSet, toRemove);
  }, [
    location.pathname,
    location.search,
    navigation.state,
    registryEpoch,
    setSearchParams,
  ]);

  return null;
}
